import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ErrorFallbackScreen from './ErrorFallbackScreen';

// useRouteError() is not always an Error — data routers can surface a
// Response (throw json()/redirect()) or any thrown value. Normalize so
// Sentry gets a real exception with a meaningful message instead of a
// "Non-Error exception captured" event, while still ALWAYS reporting.
const toError = (routeError: unknown): Error => {
  if (routeError instanceof Error) return routeError;
  if (isRouteErrorResponse(routeError)) {
    return new Error(`Route error response ${routeError.status}: ${routeError.statusText}`);
  }
  return new Error(`Non-Error route error: ${String(routeError)}`);
};

/**
 * TELV1-489 — errorElement for the data router. React Router catches
 * route render errors with its own boundary BEFORE the class
 * ErrorBoundary wrapping RouterProvider can see them, so without this
 * element respondents got React Router's default stack-trace page and
 * Sentry never received the error (the screening-index crash shipped for
 * weeks with zero events). Reports once per error, then renders the same
 * branded recovery screen as ErrorBoundary.
 */
function RouteErrorFallback() {
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(toError(error), { tags: { error_boundary: 'router' } });
  }, [error]);

  return <ErrorFallbackScreen onReload={() => window.location.reload()} />;
}

export default RouteErrorFallback;
